import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightsTimeSeriesBucket } from '@brand24-original/llm-types';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { useTheme } from '../../../../shared/contexts/theme/ThemeContext';
import { PeriodType } from '../../../../shared/types/prompt/PeriodType';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { getInsightsMetricColors } from '../../helpers/insightsChartColors';
import InsightsTrendChart, { InsightsTrendSeries } from '../insights-trend-chart/InsightsTrendChart';

interface VisibilityCitedChartProps {
  buckets: InsightsTimeSeriesBucket[] | null;
  period: PeriodType;
  isLoading: boolean;
  isError?: boolean;
}

const VisibilityCitedChart: React.FC<VisibilityCitedChartProps> = ({
  buckets,
  period,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Literal hexes of the KPI accents (--primary-ux-500 / --green-100); theme passed explicitly so
  // useMemo has a real dependency.
  const colors = useMemo(() => getInsightsMetricColors(theme), [theme]);

  const series: InsightsTrendSeries[] = useMemo(() => {
    const data = buckets ?? [];
    // null cited = the brand's domains are unknown (whole series) — no "Cited" line at all, never 0%.
    const hasCited = data.some((bucket) => bucket.cited !== null);
    return [
      {
        name: t('insightPage.timeSeries.legendVisibility'),
        color: colors.visibility,
        areaAlpha: 0.28,
        data: data.map((bucket) => percentOf(bucket.visible, bucket.total)),
      },
      ...(hasCited
        ? [
            {
              name: t('insightPage.timeSeries.legendCited'),
              color: colors.cited,
              areaAlpha: 0.24,
              data: data.map((bucket) =>
                bucket.cited !== null ? percentOf(bucket.cited, bucket.total) : null
              ),
            },
          ]
        : []),
    ];
  }, [buckets, colors, t]);

  return (
    <div className="stats__panel visibility-cited-chart">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.timeSeries.title')}
          <CustomTooltip tooltipText="insightPage.timeSeries.tooltip" addMargin={false} />
        </div>
      </div>
      <div className="insights-trend-chart__card">
        {isLoading ? (
          <div className="insights-trend-chart__skeleton" />
        ) : isError ? (
          <EmptyPanel textKey="somethingWentWrong" />
        ) : !buckets || buckets.length === 0 ? (
          <EmptyPanel />
        ) : (
          <InsightsTrendChart buckets={buckets} period={period} series={series} />
        )}
      </div>
    </div>
  );
};

export default VisibilityCitedChart;
